import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X, Link2, Check, Lock, Loader2, Camera } from 'lucide-react'
import { createSnapshot, snapshotUrl, type CreateSnapshotInput } from '../lib/snapshots'
import ShareBar from './ShareBar'
import { toast } from '../lib/toast'
import Dropdown from './Dropdown'
import { useFocusTrap } from '../lib/useFocusTrap'

interface Props {
  open: boolean
  onClose: () => void
  type: CreateSnapshotInput['type']
  title?: string
  domain?: string
  sourceId?: number | string | null
  /** Vrací data ke zmrazení — voláno až při kliknutí na „Vytvořit odkaz" (čerstvá data). */
  getPayload: () => any
}

/**
 * Modal pro sdílení libovolné analýzy jako zmrazený snapshot.
 * Uživatel volitelně nastaví heslo a expiraci, vytvoří odkaz a nasdílí ho.
 */
export default function ShareSnapshotModal({ open, onClose, type, title, domain, sourceId, getPayload }: Props) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [expiry, setExpiry] = useState('0') // 0 = bez expirace
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  useFocusTrap(cardRef, onClose, open)

  if (!open) return null

  const url = token ? snapshotUrl(token) : ''

  const reset = () => {
    setPassword(''); setExpiry('0'); setToken(null); setCopied(false)
  }
  const close = () => { reset(); onClose() }

  const create = async () => {
    const payload = getPayload()
    if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
      toast.error(t('snapshot.empty_payload', { defaultValue: 'Není co sdílet.' }))
      return
    }
    setLoading(true)
    try {
      const res = await createSnapshot({
        type, title, domain, source_id: sourceId ?? null, payload,
        password: password.trim() || undefined,
        expires_in_days: Number(expiry) || undefined,
      })
      setToken(res.token)
      if (res.reused) toast.success(t('snapshot.reused', { defaultValue: 'Odkaz pro tuto analýzu už existoval.' }))
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('snapshot.create_error', { defaultValue: 'Odkaz se nepodařilo vytvořit.' }))
    } finally { setLoading(false) }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      toast.success(t('share.copied', { defaultValue: 'Odkaz zkopírován' }))
      setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }

  const expiryOptions = [
    { value: '0', label: t('snapshot.expiry_never', { defaultValue: 'Bez expirace' }) },
    { value: '7', label: t('snapshot.expiry_7', { defaultValue: '7 dní' }) },
    { value: '30', label: t('snapshot.expiry_30', { defaultValue: '30 dní' }) },
    { value: '90', label: t('snapshot.expiry_90', { defaultValue: '90 dní' }) },
  ]

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Průhledný click-catcher pro zavření klikem mimo — bez tmavého pozadí (na přání).
          Není to ovládací prvek (zavřít jde i ✕ a Esc), proto aria-hidden + vypnuté pravidlo. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div aria-hidden="true" className="absolute inset-0" onClick={close} />
      <div ref={cardRef} role="dialog" aria-modal="true" className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-white/10 p-6">
        <button onClick={close} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
          <Camera className="w-5 h-5 text-primary-600" />
          {t('snapshot.share_title', { defaultValue: 'Sdílet analýzu' })}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          {t('snapshot.share_desc', { defaultValue: 'Vytvoří zmrazený snímek — kdokoli s odkazem ho uvidí přesně v tomto stavu, ve svém jazyce.' })}
        </p>

        {!token ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Lock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                {t('snapshot.password_label', { defaultValue: 'Heslo (volitelné)' })}
              </label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('snapshot.password_ph', { defaultValue: 'Ponechte prázdné pro veřejný odkaz' })}
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {t('snapshot.expiry_label', { defaultValue: 'Platnost odkazu' })}
              </label>
              <Dropdown value={expiry} onChange={setExpiry} options={expiryOptions} />
            </div>
            <button
              onClick={create}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              {t('snapshot.create_link', { defaultValue: 'Vytvořit odkaz' })}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                className="flex-1 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-white/15 bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-gray-200 text-sm outline-none"
              />
              <button
                onClick={copy}
                className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary-600 text-white hover:bg-primary-700 transition"
                aria-label={t('share.copy_link', { defaultValue: 'Kopírovat odkaz' })}
              >
                {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
              </button>
            </div>
            {password.trim() && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {t('snapshot.password_set', { defaultValue: 'Odkaz je chráněn heslem — sdělte ho příjemci zvlášť.' })}
              </p>
            )}
            <div className="pt-1 border-t border-gray-100 dark:border-white/10">
              <ShareBar url={url} title={title} className="mt-3" />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t('snapshot.revoke_hint', { defaultValue: 'Sdílení můžete kdykoli zrušit v Historii.' })}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
